import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetch_snd_country_map_by_id,
  fetch_snd_region_map_by_id,
  fetch_snd_area_map_by_id,
  fetch_snd_territory_map_by_id,
  fetch_distribution_house_summary_data,
  fetch_high_balance_agent_summary_data,
  fetch_dh_stock_balance_lifting_summary_data,
  fetch_dh_stock_progress_summary_data,
  fetch_dh_stock_balance_agent_summary_data,
  fetch_agent_with_transaction_summary_data,
  fetch_leading_agent_summary_data,
  fetch_leading_agent_tpv_summary_data,
  fetch_leading_agent_category_summary_data,
  fetch_milking_agent_summary_data,
  fetch_active_agent_summary_data,
  fetch_active_agent_without_disbursement_summary_data,
  fetch_active_agent_partner_payment_only_summary_data,
  fetch_territory_distribution_houses,
  fetch_territory_high_balance_agents,
  fetch_territory_dh_stock_balance_liftings,
  fetch_territory_dh_stock_progress,
  fetch_territory_dh_stock_balance_agents,
  fetch_territory_active_agents,
  fetch_territory_active_agents_without_disbursement,
  fetch_territory_active_agents_partner_payment,
  fetch_territory_agents_with_transaction,
  fetch_territory_leading_agents,
  fetch_territory_leading_agents_tpv,
  fetch_territory_gold_agents,
  fetch_territory_silver_agents,
  fetch_territory_bronze_agents,
  fetch_territory_milking_agents
} from './actions';
import {
  forward_fetch_by_selected_state,
  backward_fetch_by_selected_state
} from '../actions';
import { getCkuMapData } from './reducers';
import {
  getSndCountryDetails,
  getSndRegionDetails,
  getSndAreaDetails,
  getSndTerritoryDetails,
  getSelectedState,
  getFilteringState,
  getFilterType,
  getFilteringDateRange,
  COUNTRY,
  REGION,
  AREA,
  TERRITORY
} from '../reducers';
import {
  make_choropleth,
  make_boundary,
  make_label_by_state,
  make_legends_by_state,
  make_summary_by_state,
  make_markers_by_state,
  merge_regional_data,
  merge_regional_marker_data
} from './utils';
import Map from '../../../Map';

export default function(props) {
  const [choropleth, setChoropleth] = useState(null);
  const [boundary, setBoundary] = useState(null);
  const [label, setLabel] = useState(null);
  const [legends, setLegends] = useState(null);
  const [summary, setSummary] = useState(null);
  const [markers, setMarkers] = useState(null);
  const dispatch = useDispatch();
  const countryDetails = useSelector(getSndCountryDetails);
  const regionDetails = useSelector(getSndRegionDetails);
  const areaDetails = useSelector(getSndAreaDetails);
  const territoryDetails = useSelector(getSndTerritoryDetails);
  const selectedState = useSelector(getSelectedState);
  const filteringState = useSelector(getFilteringState);
  const filterType = useSelector(getFilterType);
  const filteringDateRange = useSelector(getFilteringDateRange);
  const ckuMapData = useSelector(getCkuMapData);

  const getSelectedStateId = () => {
    let id = null;
    switch (selectedState) {
      case COUNTRY:
        id = countryDetails.id;
        break;
      case REGION:
        id = regionDetails.id;
        break;
      case AREA:
        id = areaDetails.id;
        break;
      case TERRITORY:
        id = territoryDetails.id;
        break;
      default:
        id = null;
    }
    return id;
  };

  const getQueryFilter = () => {
    switch (selectedState) {
      case COUNTRY:
        return 'territory__area__region__country';
      case REGION:
        return 'territory__area__region';
      case AREA:
        return 'territory__area';
      case TERRITORY:
        return 'territory';
      default:
        return '';
    }
    return '';
  };

  const onClickRegion = event => {
    const id = event.target.feature.properties.id;
    dispatch(forward_fetch_by_selected_state(id, selectedState));
  };

  const onRightClick = event => {
    dispatch(backward_fetch_by_selected_state(selectedState));
  };

  useEffect(() => {
    if (!countryDetails.map) return;
    dispatch(fetch_snd_country_map_by_id(countryDetails.map));
  }, [countryDetails]);

  useEffect(() => {
    if (!regionDetails.map) return;
    dispatch(fetch_snd_region_map_by_id(regionDetails.map));
  }, [regionDetails]);

  useEffect(() => {
    if (!areaDetails.map) return;
    dispatch(fetch_snd_area_map_by_id(areaDetails.map));
  }, [areaDetails]);

  useEffect(() => {
    if (!territoryDetails.map) return;
    dispatch(fetch_snd_territory_map_by_id(territoryDetails.map));
  }, [territoryDetails]);

  useEffect(() => {
    //Doing api calls here
    if (!selectedState) return;
    const id = getSelectedStateId();
    const date = filteringDateRange.format('YYYY-MM-DD');
    const filter = getQueryFilter();
    console.log('hey time for api call', filter);
    if (filteringState.distribution_house) {
      dispatch(fetch_distribution_house_summary_data(selectedState, id));
      dispatch(fetch_territory_distribution_houses(id, filter));
    }

    if (filteringState.high_balance_agents) {
      dispatch(fetch_high_balance_agent_summary_data(selectedState, id));
      dispatch(fetch_territory_high_balance_agents(id, filter));
    }

    if (filteringState.dh_stock_balance_lifting) {
      dispatch(fetch_dh_stock_balance_lifting_summary_data(selectedState, id));
      dispatch(fetch_territory_dh_stock_balance_liftings(id, filter));
    }

    if (filteringState.dh_stock_progress) {
      dispatch(
        fetch_dh_stock_progress_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
      dispatch(fetch_territory_dh_stock_progress(id, filter));
    }

    if (filteringState.dh_stock_balance_agent) {
      dispatch(fetch_dh_stock_balance_agent_summary_data(selectedState, id));
      dispatch(fetch_territory_dh_stock_balance_agents(id, filter));
    }

    if (filteringState.active_agents && selectedState !== TERRITORY) {
      dispatch(
        fetch_active_agent_summary_data(selectedState, id, filterType, date)
      );
      dispatch(fetch_territory_active_agents(id, date, filter));
    }

    if (filteringState.active_agent_without_disbursement_cashout) {
      dispatch(
        fetch_active_agent_without_disbursement_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
      dispatch(
        fetch_territory_active_agents_without_disbursement(id, date, filter)
      );
    }

    if (filteringState.active_agent_partner_payment_only) {
      dispatch(
        fetch_active_agent_partner_payment_only_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
      dispatch(fetch_territory_active_agents_partner_payment(id, date, filter));
    }

    if (filteringState.agent_with_transaction) {
      dispatch(fetch_agent_with_transaction_summary_data(selectedState, id));
      dispatch(fetch_territory_agents_with_transaction(id, filter));
    }

    if (filteringState.leading_agents) {
      dispatch(
        fetch_leading_agent_summary_data(selectedState, id, filterType, date)
      );
      dispatch(fetch_territory_leading_agents(id, filterType, date, filter));
    }

    if (filteringState.leading_agent_tpv) {
      dispatch(
        fetch_leading_agent_tpv_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
      dispatch(
        fetch_territory_leading_agents_tpv(id, filterType, date, filter)
      );
    }

    if (filteringState.leading_agent_category) {
      dispatch(
        fetch_leading_agent_category_summary_data(
          selectedState,
          id,
          filterType,
          date
        )
      );
      dispatch(fetch_territory_gold_agents(id, filterType, date, filter));
      dispatch(fetch_territory_silver_agents(id, filterType, date, filter));
      dispatch(fetch_territory_bronze_agents(id, filterType, date, filter));
    }

    if (filteringState.milking_agent && selectedState !== TERRITORY) {
      dispatch(
        fetch_milking_agent_summary_data(selectedState, id, filterType, date)
      );
      dispatch(fetch_territory_milking_agents(id, filterType, date, filter));
    }
  }, [selectedState, filteringState, filterType, filteringDateRange]);

  useEffect(() => {
    // Doing map data population on country, cluster, area state
    if (selectedState === TERRITORY) return;

    let regional_info = [];
    let key = null;

    switch (selectedState) {
      case COUNTRY:
        regional_info = countryDetails.regions;
        key = 'regions';
        break;
      case REGION:
        regional_info = regionDetails.areas;
        key = 'areas';
        break;
      case AREA:
        regional_info = areaDetails.territories;
        key = 'territories';
        break;
      default:
        break;
    }

    let map_data = ckuMapData.data[key];
    let distribution_house_summary = ckuMapData.distributionHouseSummary[key];
    let high_balance_agent_summary = ckuMapData.highBalanceAgentSummary[key];
    let dh_stock_balance_lifting_summary =
      ckuMapData.dhStockBalanceLiftingSummary[key];
    let dh_stock_progress_summary = ckuMapData.dhStockProgressSummary[key];
    let dh_stock_balance_agent_summary =
      ckuMapData.dhStockBalanceAgentSummary[key];
    let agent_with_transaction_summary =
      ckuMapData.agentWithTransactionSummary[key];
    let leading_agent_summary = ckuMapData.leadingAgentSummary[key];
    let leading_agent_tpv_summary = ckuMapData.leadingAgentTpvSummary[key];
    let leading_agent_category_summary =
      ckuMapData.leadingAgentCategorySummary[key];
    let milking_agent_summary = ckuMapData.milkingAgentSummary[key];
    let active_agent_summary = ckuMapData.activeAgentSummary[key];
    let active_agent_without_disbursement_summary =
      ckuMapData.activeAgentWithoutDisbursementSummary[key];
    let active_agent_partner_payment_summary =
      ckuMapData.activeAgentPartnerPaymentOnlySummary[key];

    const merge_info = merge_regional_data(
      {
        regional_info,
        distribution_house_summary,
        high_balance_agent_summary,
        dh_stock_balance_lifting_summary,
        dh_stock_progress_summary,
        dh_stock_balance_agent_summary,
        agent_with_transaction_summary,
        leading_agent_summary,
        leading_agent_tpv_summary,
        leading_agent_category_summary,
        milking_agent_summary,
        active_agent_summary,
        active_agent_without_disbursement_summary,
        active_agent_partner_payment_summary
      },
      filteringState
    );

    if (!merge_info) return;

    setChoropleth(make_choropleth(map_data, merge_info, filteringState));
    setLabel(make_label_by_state(map_data, merge_info, filteringState));
    setLegends(make_legends_by_state(filteringState));
    setSummary(make_summary_by_state(merge_info, filteringState));
    setBoundary(null);
    setMarkers(null);
  }, [ckuMapData]);

  useEffect(() => {
    // Doing map data population on TERRITORY state
    if (selectedState !== TERRITORY || !ckuMapData.data.geo_json) return;
    const boundary_map = ckuMapData.data;
    const merge_markers_info = merge_regional_marker_data(ckuMapData);
    setBoundary(make_boundary(boundary_map));
    setChoropleth(null);
    setLabel(null);
    setLegends(null);
    setSummary(null);
    setMarkers(make_markers_by_state(merge_markers_info, filteringState));
  }, [ckuMapData]);

  return (
    <Map
      choropleth={choropleth}
      boundary={boundary}
      regionLabel={label}
      regionAction={onClickRegion}
      rightClickEvent={onRightClick}
      legends={legends}
      summary={summary}
      markers={markers}
    />
  );
}
